/**
 * V7 onboarding step config — single source of truth for the 15-step flow.
 * Spec: specs/onboarding-rebuild.md (V7 amendments 2026-05-12, renumber
 * 2026-05-13, Act 1+3 restructure 2026-05-16, trust slot inserted 2026-05-17)
 *
 * Each step:
 *   id          — kebab-case identifier (matches the step folder under src/components/onboarding/steps/)
 *   number      — 1-15 ordinal in the flow
 *   act         — 'intro' | 'climax' | 'conclusion'
 *   title       — short label for admin preview + progress tracker tooltip
 *   captures    — array of profile field names this step writes (or [] if none)
 *   required    — true for 14 of 15 steps; only step 8 (ministry-map-seed) is skippable
 *   conditional — function (profile) => bool, returns true if the step should render.
 *
 * Each step renders its synthesis component (see steps/<num>-<id>/index.jsx).
 * Admin preview (/admin/onboarding-preview) lets you switch persona to test
 * different user states. Variant scaffolding retired 2026-05-14.
 *
 * 2026-05-17 (Mason lock):
 *   - Inserted Trust placeholder at slot 10 (right after Founder note) so
 *     the brand block reads as one beat: founder (who) → testimonials
 *     (who else). Placeholder until real testimonials exist. All
 *     subsequent steps (profile-data, journey-summary, reminder-picker,
 *     paywall-trial, handoff) pushed from 10-14 to 11-15.
 *   - Total: 14 → 15 steps
 *
 * 2026-05-16 restructure (Mason lock):
 *   - Dropped old step 3 (reading-rhythm) — sliders moved to profile-data bundle
 *   - Dropped old step 4 (aha-stat) — personal-math AHA was fragile
 *   - Dropped old step 14 (commitment-slider) — ceremonial, no consumer
 *   - Moved struggles (old step 5) to slot 3 so they follow goal naturally
 *   - Reminder-picker now ONLY captures reminder_method; daily_time_goal
 *     moves to profile-data bundle
 *   - Combined old steps 12 + 13 (reminder + push permission) into ONE
 *     step with a sectioned card + device-adaptive Notifications option.
 *   - Total: 18 → 14 steps (pre-trust-insertion)
 *
 * Folder names still reflect OLD numbering for now (e.g. 05-hunger-chips
 * is at step number 3). A future cleanup pass will rename folders to align.
 * 11-trust is the only NEW folder that matches its step number.
 */

export const ONBOARDING_STEPS = [
  // ───── Act 1 — INTRO: Frame the problem ─────
  {
    id: 'welcome',
    number: 1,
    act: 'intro',
    title: 'Welcome',
    captures: [],
    required: true,
  },
  {
    id: 'primary-goal',
    number: 2,
    act: 'intro',
    title: 'Primary goal',
    captures: ['primary_goal'],
    required: true,
  },
  {
    id: 'hunger-chips',
    number: 3,
    act: 'intro',
    title: "What you're hungry for",
    captures: ['current_struggles'],
    required: true,
  },
  {
    id: 'hunger-reflection',
    number: 4,
    act: 'intro',
    title: 'Reflection on hunger',
    captures: [],
    required: true,
  },

  // ───── Act 2 — CLIMAX: Prove value ─────
  {
    id: 'doctrinal-trust',
    number: 5,
    act: 'climax',
    title: 'Where Illuminate stands',
    captures: [],
    required: true,
  },
  {
    id: 'ai-demo',
    number: 6,
    act: 'climax',
    title: 'AI demo (active)',
    captures: [], // first conversation persisted to conversations + messages tables
    required: true,
  },
  {
    id: 'streak-intro',
    number: 7,
    act: 'climax',
    title: 'Day 1 streak (earned)',
    captures: ['streak_count', 'last_active_date', 'longest_streak'],
    required: true,
  },
  {
    id: 'ministry-map-seed',
    number: 8,
    act: 'climax',
    title: 'Ministry Map seed',
    captures: [], // first row in ministry_map_people if not skipped
    required: false, // SOLE EXCEPTION to no-skip rule
  },
  {
    // HIDDEN ON PROD 2026-05-17 — Mason hasn't shot the founder video yet.
    // `conditional: () => false` makes `getNextStep` skip this slot.
    // Admin preview overrides via `{ includeHidden: true }` so Mason can
    // still review the slot design when the video lands.
    id: 'founder-note',
    number: 9,
    act: 'climax',
    title: 'Founder note from Mason (hidden — needs video)',
    captures: [],
    required: true,
    conditional: () => false,
  },
  {
    // HIDDEN ON PROD 2026-05-17 — placeholder until real testimonials exist.
    // Mason locked Slot C placement (immediately after Founder note) — when
    // real testimonials are collected, drop `conditional` to expose the step.
    // Admin preview overrides via `{ includeHidden: true }` so Mason can
    // refer back to the bracket-stub design.
    id: 'trust',
    number: 10,
    act: 'climax',
    title: 'Trust / testimonials (hidden — placeholder)',
    captures: [],
    required: true,
    conditional: () => false,
  },

  // ───── Act 3 — CONCLUSION: Commit + offer ─────
  {
    id: 'profile-data',
    number: 11,
    act: 'conclusion',
    title: 'Profile data (combined)',
    captures: [
      'role',
      'denomination',
      'preferred_translation',
      'debated_topics_view',
      'interests',
      'age',
      'bible_reading_days_per_week',
      'daily_time_goal',
    ],
    required: true,
  },
  {
    id: 'journey-summary',
    number: 12,
    act: 'conclusion',
    title: "Journey summary + what's waiting",
    captures: [],
    required: true,
  },
  {
    id: 'reminder-picker',
    number: 13,
    act: 'conclusion',
    title: 'Reminder + notifications',
    captures: ['reminder_method', 'notification_preferences'],
    required: true,
  },
  {
    id: 'paywall-trial',
    number: 14,
    act: 'conclusion',
    title: 'Paywall — 7-day trial',
    captures: ['has_used_trial', 'is_trial', 'subscription_plan'],
    required: true,
  },
  {
    id: 'handoff',
    number: 15,
    act: 'conclusion',
    title: 'Handoff to /chat',
    captures: [],
    required: true,
  },
]

export const TOTAL_STEPS = ONBOARDING_STEPS.length // 15

export const STEPS_BY_ACT = {
  intro: ONBOARDING_STEPS.filter(s => s.act === 'intro'),
  climax: ONBOARDING_STEPS.filter(s => s.act === 'climax'),
  conclusion: ONBOARDING_STEPS.filter(s => s.act === 'conclusion'),
}

export function getStepByNumber(n) {
  return ONBOARDING_STEPS.find(s => s.number === n) || null
}

export function getStepById(id) {
  return ONBOARDING_STEPS.find(s => s.id === id) || null
}

/**
 * Return the next step after `currentNumber` that should render.
 *
 * `options.includeHidden = true` ignores `step.conditional` so admin preview
 * can step through every slot even when production hides them
 * (e.g., founder-note + trust placeholders 2026-05-17).
 */
export function getNextStep(currentNumber, profile, { includeHidden = false } = {}) {
  for (let n = currentNumber + 1; n <= TOTAL_STEPS; n++) {
    const step = getStepByNumber(n)
    if (!step) continue
    if (!includeHidden && step.conditional && !step.conditional(profile)) continue
    return step
  }
  return null
}

/**
 * Return the previous step before `currentNumber` that should render.
 * Same `includeHidden` flag semantics as `getNextStep`.
 */
export function getPrevStep(currentNumber, profile, { includeHidden = false } = {}) {
  for (let n = currentNumber - 1; n >= 1; n--) {
    const step = getStepByNumber(n)
    if (!step) continue
    if (!includeHidden && step.conditional && !step.conditional(profile)) continue
    return step
  }
  return null
}
